import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Editor } from "primereact/editor";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { MultiSelect } from "primereact/multiselect";
import './EmailDialog.css'
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Api } from '../../../services/api';
import { DEFAULT_EMAIL_SIGNATURE, getStoredEmailSignatureSettings } from '../../../services/emailSignature';

const EmailDialog = ({
  visible,
  onHide,
  showName,
  showEmail,
  showId,
  allSupervisors,
  toast,
}) => {
  const { instance, accounts } = useMsal();
  const defaultSignature = DEFAULT_EMAIL_SIGNATURE;
  const [customSubject, setCustomSubject] = useState("");
  const [customMessage, setCustomMessage] = useState("");
  const [signature, setSignature] = useState(() => getStoredEmailSignatureSettings().signature);
  const [includeSignature, setIncludeSignature] = useState(true);
  const [selectedCCList, setSelectedCCList] = useState([]);
  const [emailThreads, setEmailThreads] = useState([]);
  const [replyingTo, setReplyingTo] = useState(null); // stores thread/message being replied to
  const [expandedMessages, setExpandedMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const fetchEmailThreadsRef = useRef(async () => {});

  const signaturePreviewHtml = /<[^>]+>/.test(signature || "")
    ? signature
    : (signature || defaultSignature).replace(/\n/g, '<br/>');

  const normalizeSignatureMarkup = (markup) => {
    if (!markup) {
      return "";
    }

    return markup
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<!--([\s\S]*?)-->/g, "")
      .trim();
  };

  const getMessagePreviewText = (markup) => {
    if (!markup) {
      return "No Content";
    }

    const plainText = String(markup)
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/p>/gi, " ")
      .replace(/<\/div>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (!plainText) {
      return /<img[^>]*>/i.test(markup) ? "[Email contains an inline image]" : "No Content";
    }

    const words = plainText.split(" ");
    return words.length > 40 ? `${words.slice(0, 40).join(" ")}...` : plainText;
  };

  const getMessagePreviewImage = (markup) => {
    if (!markup) {
      return "";
    }

    const imageMatch = String(markup).match(/<img[^>]+src=["']([^"']+)["'][^>]*>/i);
    return imageMatch ? imageMatch[1] : "";
  };

  const applySavedSignaturePreviewFallback = (markup) => {
    if (!markup || !/<img[^>]*>/i.test(markup)) {
      return markup;
    }

    const savedSignatureImageSrc = getMessagePreviewImage(signaturePreviewHtml);
    if (!savedSignatureImageSrc) {
      return markup;
    }

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(markup, 'text/html');
      const root = parsedDocument.body;

      root.querySelectorAll('img').forEach((image) => {
        const currentSrc = (image.getAttribute('src') || '').trim().toLowerCase();
        if (!currentSrc || currentSrc.startsWith('cid:') || currentSrc.includes('outlook.office.com') || currentSrc.includes('office365.com') || currentSrc.includes('attachments.office.net')) {
          image.setAttribute('src', savedSignatureImageSrc);
          image.removeAttribute('originalsrc');
        }
      });

      return root.innerHTML;
    } catch (error) {
      console.error('Signature preview fallback error:', error);
      return markup;
    }
  };

  const normalizeHistoryImagesForDisplay = (markup) => {
    if (!markup || !/<img[^>]*>/i.test(markup)) {
      return markup;
    }

    const savedSignatureImageSrc = getMessagePreviewImage(signaturePreviewHtml);

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(markup, 'text/html');
      const root = parsedDocument.body;

      root.querySelectorAll('img').forEach((image) => {
        const currentSrc = (image.getAttribute('src') || '').trim();
        const normalizedSrc = currentSrc.toLowerCase();
        const looksLikeHostedAttachment =
          !currentSrc ||
          normalizedSrc.startsWith('cid:') ||
          normalizedSrc.includes('mail.google.com') ||
          normalizedSrc.includes('outlook.office.com') ||
          normalizedSrc.includes('outlook.office365.com') ||
          normalizedSrc.includes('attachments.office.net');

        if (looksLikeHostedAttachment && savedSignatureImageSrc) {
          image.setAttribute('src', savedSignatureImageSrc);
        }

        if (savedSignatureImageSrc) {
          const safeSrc = savedSignatureImageSrc.replace(/'/g, "\\'");
          image.setAttribute('onerror', `this.onerror=null;this.src='${safeSrc}';`);
        } else {
          image.setAttribute('onerror', "this.style.display='none';");
        }

        image.removeAttribute('originalsrc');
      });

      return root.innerHTML;
    } catch (error) {
      console.error('History image normalization error:', error);
      return applySavedSignaturePreviewFallback(markup);
    }
  };

  const getLatestEmailMessageHtml = (markup) => {
    if (!markup) {
      return "";
    }

    let cleanedMarkup = String(markup)
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/<style[\s\S]*?<\/style>/gi, "")
      .replace(/<!--([\s\S]*?)-->/g, "")
      .trim();

    const bodyMatch = cleanedMarkup.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    if (bodyMatch) {
      cleanedMarkup = bodyMatch[1];
    }

    const separators = [
      /<div[^>]*class=["'][^"']*gmail_quote[^"']*["'][^>]*>[\s\S]*$/i,
      /<div[^>]*id=["']divRplyFwdMsg["'][^>]*>[\s\S]*$/i,
      /<hr[^>]*id=["']replySplit["'][^>]*>[\s\S]*$/i,
      /<hr[^>]*>[\s\S]*$/i,
      /<blockquote[\s\S]*$/i,
      /On\s.+?wrote:[\s\S]*$/i,
      /From:\s.+?Sent:\s.+/is,
    ];

    separators.forEach((separator) => {
      cleanedMarkup = cleanedMarkup.replace(separator, "").trim();
    });

    return cleanedMarkup || String(markup);
  };

  const messageNeedsExpansion = (markup) => {
    if (!markup) {
      return false;
    }

    const plainText = String(markup)
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/p>/gi, " ")
      .replace(/<\/div>/gi, " ")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return plainText.split(" ").filter(Boolean).length > 40 || /<img[^>]*>/i.test(markup);
  };

  const fetchImageAsInlineAttachment = async (imageUrl, attachmentIndex) => {
    const response = await fetch(imageUrl);

    if (!response.ok) {
      throw new Error(`Unable to fetch image: ${response.status}`);
    }

    const imageBlob = await response.blob();

    const contentBytes = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result || '';
        const base64Content = String(result).split(',')[1] || '';
        if (!base64Content) {
          reject(new Error('Image conversion failed'));
          return;
        }
        resolve(base64Content);
      };
      reader.onerror = () => reject(new Error('Image conversion failed'));
      reader.readAsDataURL(imageBlob);
    });

    const mimeType = imageBlob.type || 'image/png';
    const extension = mimeType.split('/')[1]?.split('+')[0] || 'png';
    const contentId = `signature-image-${attachmentIndex}-${Date.now()}`;

    return {
      attachment: {
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: `signature-image-${attachmentIndex}.${extension}`,
        contentType: mimeType,
        contentBytes,
        isInline: true,
        contentId,
        contentLocation: contentId,
      },
      contentId,
    };
  };

  const fetchFullMessageBody = async (token, messageId, fallbackContent) => {
    try {
      const encodedMessageId = encodeURIComponent(messageId);
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${encodedMessageId}?$select=body,uniqueBody`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return fallbackContent;
      }

      const data = await response.json();
      return data.body?.content || data.uniqueBody?.content || fallbackContent;
    } catch (error) {
      console.error('Error fetching full message body:', error);
      return fallbackContent;
    }
  };

  const resolveInlineAttachmentImages = async (token, messageId, htmlContent) => {
    if (!htmlContent || !/<img[^>]*>/i.test(htmlContent)) {
      return htmlContent;
    }

    const normalizeInlineReference = (value) => String(value || '')
      .trim()
      .replace(/^cid:/i, '')
      .replace(/^<|>$/g, '')
      .trim()
      .toLowerCase();

    try {
      const encodedMessageId = encodeURIComponent(messageId);
      const response = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${encodedMessageId}/attachments?$select=id,name,contentType,isInline,contentId,contentLocation`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        return htmlContent;
      }

      const data = await response.json();
      const attachments = Array.isArray(data.value) ? data.value : [];
      const inlineAttachments = await Promise.all(attachments
        .filter((attachment) => {
          const isImageAttachment = String(attachment.contentType || '').toLowerCase().startsWith('image/');
          return isImageAttachment && (attachment.isInline || attachment.contentId || attachment.contentLocation || attachment.contentBytes);
        })
        .map(async (attachment) => {
          if (attachment.contentBytes) {
            return attachment;
          }

          try {
            const encodedAttachmentId = encodeURIComponent(attachment.id);
            const attachmentResponse = await fetch(`https://graph.microsoft.com/v1.0/me/messages/${encodedMessageId}/attachments/${encodedAttachmentId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!attachmentResponse.ok) {
              return null;
            }

            const attachmentData = await attachmentResponse.json();
            return attachmentData.contentBytes ? attachmentData : null;
          } catch (error) {
            console.error('Error loading attachment details:', error);
            return null;
          }
        }));

      const usableInlineAttachments = inlineAttachments.filter((attachment) => attachment && attachment.contentBytes);

      if (!usableInlineAttachments.length) {
        return htmlContent;
      }

      const inlineAttachmentMap = new Map();

      usableInlineAttachments.forEach((attachment) => {
        const replacementSource = `data:${attachment.contentType || 'image/png'};base64,${attachment.contentBytes}`;
        const references = [attachment.contentId, attachment.contentLocation]
          .map(normalizeInlineReference)
          .filter(Boolean);

        references.forEach((reference) => {
          inlineAttachmentMap.set(reference, replacementSource);
        });
      });

      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(htmlContent, 'text/html');
      const root = parsedDocument.body;

      if (!inlineAttachmentMap.size) {
        return root.innerHTML;
      }

      const unmatchedAttachmentSources = [];

      root.querySelectorAll('img').forEach((image) => {
        const src = image.getAttribute('src') || '';
        const originalSrc = image.getAttribute('originalsrc') || '';
        const sourceCandidates = [src, originalSrc]
          .map(normalizeInlineReference)
          .filter(Boolean);

        const resolvedSource = sourceCandidates
          .map((candidate) => inlineAttachmentMap.get(candidate))
          .find(Boolean);

        if (resolvedSource) {
          image.setAttribute('src', resolvedSource);
          image.removeAttribute('originalsrc');
          inlineAttachmentMap.forEach((value, key) => {
            if (value === resolvedSource) {
              inlineAttachmentMap.delete(key);
            }
          });
        }
      });

      inlineAttachmentMap.forEach((value) => {
        unmatchedAttachmentSources.push(value);
      });

      if (unmatchedAttachmentSources.length) {
        const previewImages = Array.from(root.querySelectorAll('img')).filter((image) => {
          const src = (image.getAttribute('src') || '').trim().toLowerCase();
          return !src || src.startsWith('cid:') || src.includes('outlook.office.com') || src.includes('office365.com') || src.includes('attachments.office.net');
        });

        previewImages.forEach((image, index) => {
          const fallbackSource = unmatchedAttachmentSources[index];
          if (!fallbackSource) {
            return;
          }

          image.setAttribute('src', fallbackSource);
          image.removeAttribute('originalsrc');
        });
      }

      return root.innerHTML;
    } catch (error) {
      console.error('Error resolving inline attachments:', error);
      return htmlContent;
    }
  };

  const prepareSignatureForEmail = async (rawSignature) => {
    if (!rawSignature) {
      return {
        html: "",
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }

    const normalizedSignature = normalizeSignatureMarkup(rawSignature);
    const looksLikeHtml = /<[^>]+>/.test(normalizedSignature);

    if (!looksLikeHtml) {
      return {
        html: normalizedSignature.replace(/\n/g, '<br/>'),
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }

    try {
      const parser = new DOMParser();
      const parsedDocument = parser.parseFromString(normalizedSignature, 'text/html');
      const root = parsedDocument.body;
      const attachments = [];
      let removedImages = 0;
      let unresolvedRemoteImages = 0;

      root.querySelectorAll('script, style, meta, title, link, xml, o\\:p').forEach((node) => node.remove());

      root.querySelectorAll('*').forEach((element) => {
        ['class', 'id', 'data-ogsb', 'data-outlook-id'].forEach((attribute) => {
          element.removeAttribute(attribute);
        });
      });

      const images = Array.from(root.querySelectorAll('img'));

      for (const image of images) {
        const rawSrc = (image.getAttribute('src') || '').trim();
        const src = rawSrc.toLowerCase();

        if (src.startsWith('data:image/')) {
          const match = rawSrc.match(/^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i);
          if (!match) {
            removedImages += 1;
            image.remove();
            continue;
          }

          const mimeType = match[1];
          const contentBytes = match[2].replace(/\s/g, '');
          const extension = mimeType.split('/')[1]?.split('+')[0] || 'png';
          const contentId = `signature-image-${attachments.length + 1}-${Date.now()}`;

          attachments.push({
            '@odata.type': '#microsoft.graph.fileAttachment',
            name: `signature-image-${attachments.length + 1}.${extension}`,
            contentType: mimeType,
            contentBytes,
            isInline: true,
            contentId,
            contentLocation: contentId,
          });

          image.setAttribute('src', `cid:${contentId}`);
          continue;
        }

        if (!src || src.startsWith('file:') || src.startsWith('blob:')) {
          removedImages += 1;
          image.remove();
          continue;
        }

        if (src.startsWith('cid:')) {
          continue;
        }

        if (/^https?:\/\//i.test(rawSrc)) {
          try {
            const { attachment, contentId } = await fetchImageAsInlineAttachment(rawSrc, attachments.length + 1);
            attachments.push(attachment);
            image.setAttribute('src', `cid:${contentId}`);
          } catch (error) {
            unresolvedRemoteImages += 1;
          }
          continue;
        }

        if (!/^https?:\/\//i.test(rawSrc)) {
          removedImages += 1;
          image.remove();
        }
      }

      root.querySelectorAll('table, tbody, tr, td, div, p, span').forEach((element) => {
        const hasRenderableChild = Array.from(element.querySelectorAll('img, br, hr')).length > 0;
        const textContent = (element.textContent || '').replace(/\u00a0/g, ' ').trim();
        if (!hasRenderableChild && textContent === '') {
          element.remove();
        }
      });

      return {
        html: root.innerHTML.trim(),
        attachments,
        removedImages,
        unresolvedRemoteImages,
      };
    } catch (error) {
      console.error('Signature normalization error:', error);
      return {
        html: normalizedSignature,
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    }
  };

  const loginToMicrosoft = async () => {
      try {
          await instance.loginPopup({
              scopes: ["Mail.Read", "Mail.Send", "Mail.ReadWrite"],
              prompt: "select_account"
          });
      } catch (e) {
          console.error(e);
          toast.current.show({ severity: 'error', detail: "Microsoft Login Failed: " + e.message });
      }
  };

  const getAccessToken = async () => {
    if (accounts.length === 0) {
        await loginToMicrosoft();
        if (accounts.length === 0) return null; // Still not logged in
    }

    const request = {
      scopes: ["Mail.Read", "Mail.Send", "Mail.ReadWrite"],
      account: accounts[0]
    };

    try {
      const response = await instance.acquireTokenSilent(request);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return instance.acquireTokenPopup(request).then((response) => {
          return response.accessToken;
        }).catch((error) => {
          toast.current.show({ severity: 'error', detail: "Error acquiring token: " + error.message });
          return null;
        });
      } else {
        toast.current.show({ severity: 'error', detail: "Error acquiring token: " + error.message });
        return null;
      }
    }
  };

  useEffect(() => {
    if (visible && showEmail) {
      fetchEmailThreadsRef.current();
      setReplyingTo(null); // reset reply when dialog opens
    }
  }, [visible, showEmail, accounts]);

  useEffect(() => {
    if (visible) {
      setSignature(getStoredEmailSignatureSettings().signature);
    }
  }, [visible]);

  fetchEmailThreadsRef.current = async () => {
    // If we're not logged in, we can't fetch. Just return. A button will be shown in UI.
    if (!accounts || accounts.length === 0) return;
    
    setLoading(true);
    const token = await getAccessToken();
    if (!token) {
        setLoading(false);
        return; 
    }

    try {
      if (!showEmail) {
          console.warn("fetchEmailThreads: showEmail is empty");
          return;
      }
      const targetEmail = showEmail.trim().toLowerCase(); // Ensure no whitespace and consistent casing
      console.log("Fetching emails for:", targetEmail);

      // Use $search because $filter on collections (toRecipients) is not supported in Graph API v1.0
      // We use KQL (Keyword Query Language) to search all participants.
      // Note: usage of $search requires "ConsistencyLevel: eventual" header.
      const searchQuery = `"participants:${targetEmail}"`;
      
      const url = `https://graph.microsoft.com/v1.0/me/messages?$search=${encodeURIComponent(searchQuery)}&$top=50&$select=id,conversationId,subject,body,from,toRecipients,ccRecipients,sentDateTime,receivedDateTime`;
      
      console.log("Graph API URL:", url);

      const response = await fetch(url, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "ConsistencyLevel": "eventual" 
          }
      });
      
      if (!response.ok) {
          const errText = await response.text();
          console.error("Graph API Error:", response.status, errText);
          throw new Error(`Graph API returned ${response.status}: ${errText}`);
      }


      if (response.ok) {
          const data = await response.json();
          // Sort client-side because $orderby is not supported with $search
          const sortedMessages = data.value.sort((a, b) => new Date(b.receivedDateTime) - new Date(a.receivedDateTime));
          const messages = await Promise.all(sortedMessages.map(async (message) => {
              const fullBodyContent = await fetchFullMessageBody(token, message.id, message.body?.content || '');
              return {
                ...message,
                body: {
                  ...message.body,
                  content: await resolveInlineAttachmentImages(token, message.id, fullBodyContent),
                },
              };
          }));

          // Group by conversationId
          const grouped = {};
          messages.forEach(msg => {
              if (!grouped[msg.conversationId]) {
                  grouped[msg.conversationId] = {
                      thread_number: msg.conversationId, // unique ID
                      subject: msg.subject,
                      messages: []
                  };
              }
              
              // Map Graph message to UI structure
                const isOutgoingMessage = msg.from?.emailAddress?.address?.toLowerCase() !== showEmail.toLowerCase();
                const latestMessageBody = getLatestEmailMessageHtml(msg.body.content);
                const normalizedMessageBody = normalizeHistoryImagesForDisplay(latestMessageBody);
                const defaults = {
                  _id: msg.id,
                  direction: isOutgoingMessage ? 'outgoing' : 'incoming',
                  from_email: msg.from?.emailAddress?.address,
                  to_email: msg.toRecipients?.map(r => r.emailAddress.address) || [],
                  send_at: msg.sentDateTime || msg.receivedDateTime,
                  message: isOutgoingMessage ? applySavedSignaturePreviewFallback(normalizedMessageBody) : normalizedMessageBody,
                  subject: msg.subject
              };
              grouped[msg.conversationId].messages.push(defaults);
          });

          // Convert to array and enforce newest-first ordering
          const threads = Object.values(grouped)
            .map((t) => {
              const sortedMessagesInThread = [...t.messages].sort(
                (a, b) => getMessageTimeValue(b.send_at) - getMessageTimeValue(a.send_at)
              );
              return {
                ...t,
                messages: sortedMessagesInThread,
                latestMessageAt: sortedMessagesInThread[0]?.send_at || null,
              };
            })
            .sort((a, b) => getMessageTimeValue(b.latestMessageAt) - getMessageTimeValue(a.latestMessageAt))
            .map((t, index) => ({
              ...t,
              thread_number: index + 1
            }));
          
          setEmailThreads(threads);
      }
    } catch (error) {
      console.error("Error fetching email history:", error);
      // Fallback to empty or toast error
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedMessages((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getMessageTimeValue = (value) => {
    const parsed = new Date(value || 0).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getUserIdFromOption = (item) => item?.user_id || item?.id || null;

  const sendViaBackendFallback = async (emailContent) => {
    const token = localStorage.getItem('authToken');
    const currentUserId = localStorage.getItem('user_id');

    const recipientIds = [showId, ...selectedCCList.map((item) => getUserIdFromOption(item))]
      .filter((id) => id !== null && id !== undefined && String(id).trim() !== '')
      .map((id) => String(id));

    const body = {
      user_id: currentUserId,
      recipient_ids: Array.from(new Set(recipientIds)),
      subject: customSubject,
      message: emailContent,
      to_email: showEmail,
      cc_emails: selectedCCList.map((item) => item?.email).filter(Boolean)
    };

    const response = await fetch(Api.API_BASE_URL + 'crm/send-email-assignee', {
      method: 'POST',
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || (data?.error && data.error !== '')) {
      throw new Error(data?.error || `Fallback API failed (${response.status})`);
    }

    return data;
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!customSubject || !customMessage) return;

    // TODO: Graph API Implementation
    const token = await getAccessToken();
    if (!token) return;

    // Logic to strip <p> tags from signature if duplicate
    const preparedSignature = includeSignature
      ? await prepareSignatureForEmail(signature || defaultSignature)
      : {
        html: "",
        attachments: [],
        removedImages: 0,
        unresolvedRemoteImages: 0,
      };
    const finalSignature = preparedSignature.html;
    const emailContent = finalSignature ? `${customMessage}<br/><br/>${finalSignature}` : customMessage;
    
    const emailBody = {
        message: {
            subject: customSubject,
            body: {
                contentType: "HTML",
          content: emailContent
            },
            toRecipients: [
                {
                    emailAddress: {
                        address: showEmail // Use actual email, not name
                    }
                }
            ],
            ccRecipients: selectedCCList.map(u => ({
                emailAddress: {
                    address: u.email // Ensure user object has email property
                }
            })),
            ...(preparedSignature.attachments.length
              ? { attachments: preparedSignature.attachments }
              : {})
        },
        saveToSentItems: "true"
    };

    const sendMailRequest = async (payload) => {
      const response = await fetch("https://graph.microsoft.com/v1.0/me/sendMail", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        let apiMessage = response.statusText;
        try {
          const errorData = await response.json();
          apiMessage = errorData?.error?.message || apiMessage;
        } catch (parseError) {
          const fallbackText = await response.text();
          apiMessage = fallbackText || apiMessage;
        }
        throw new Error(apiMessage || `Graph API error (${response.status})`);
      }
    };

    try {
      await sendMailRequest(emailBody);

      if (true) {
          toast.current.show({
            severity: "success",
            detail: "Email sent via Microsoft Graph!",
            life: 3000,
          });

          if (includeSignature && preparedSignature.removedImages > 0) {
            toast.current.show({
              severity: 'warn',
              detail: 'Some signature images used unsupported local sources and were removed. Use a normal https image URL or paste a base64 image in HTML mode.',
              life: 5000,
            });
          }

          if (includeSignature && preparedSignature.unresolvedRemoteImages > 0) {
            toast.current.show({
              severity: 'info',
              detail: 'Some signature images could not be embedded, so the original https image URLs were kept. They will render only if the recipient can access them.',
              life: 6000,
            });
          }

          // Optimistically update UI
          // Since we might not have conversation ID, we might just prepend to top thread or force refresh
          // For simplicity, let's refresh threads in a few seconds or add to UI manually
          // Actually, since we don't have conversationId from Graph response for sent item immediately, 
          // we can just refetch threads.
          setTimeout(() => fetchEmailThreadsRef.current(), 2000);

          setCustomSubject("");
          setCustomMessage("");
          setSelectedCCList([]);
          setReplyingTo(null);

      }
    } catch (error) {
      console.error("Error sending email:", error);
      const isFetchStyleError = /failed to fetch|networkerror|network request failed/i.test(String(error?.message || ""));
      const hasInlineAttachments = preparedSignature.attachments.length > 0;

      if (includeSignature && hasInlineAttachments && isFetchStyleError) {
        try {
          const fallbackSignatureHtml = normalizeSignatureMarkup(signature || defaultSignature)
            .replace(/<img[^>]*>/gi, '')
            .trim();
          const fallbackContent = fallbackSignatureHtml ? `${customMessage}<br/><br/>${fallbackSignatureHtml}` : customMessage;
          const fallbackPayload = {
            message: {
              subject: customSubject,
              body: {
                contentType: "HTML",
                content: fallbackContent
              },
              toRecipients: [
                {
                  emailAddress: {
                    address: showEmail
                  }
                }
              ],
              ccRecipients: selectedCCList.map(u => ({
                emailAddress: {
                  address: u.email
                }
              }))
            },
            saveToSentItems: "true"
          };

          await sendMailRequest(fallbackPayload);

          toast.current.show({
            severity: "warn",
            detail: "Email sent, but signature image could not be embedded. Sent with text signature fallback.",
            life: 5000,
          });

          setTimeout(() => fetchEmailThreadsRef.current(), 2000);
          setCustomSubject("");
          setCustomMessage("");
          setSelectedCCList([]);
          setReplyingTo(null);
          return;
        } catch (retryError) {
          console.error("Fallback send failed:", retryError);
        }
      }

      try {
        await sendViaBackendFallback(emailContent);
        toast.current.show({
          severity: "warn",
          detail: "Email sent via CRM fallback because Microsoft Graph was unreachable.",
          life: 5000,
        });

        setTimeout(() => fetchEmailThreadsRef.current(), 2000);
        setCustomSubject("");
        setCustomMessage("");
        setSelectedCCList([]);
        setReplyingTo(null);
        return;
      } catch (fallbackError) {
        console.error('Backend fallback error:', fallbackError);
      }

      toast.current.show({
        severity: "error",
        detail: "Failed to send email: " + error.message,
        life: 3000,
      });
    }
  };

  const dialogHeader = (
    <div className="flex align-items-center gap-2">
      <i className="pi pi-envelope text-primary text-xl"></i>
      <span className="text-xl font-semibold text-900">
        Email Correspondence with {showName}
      </span>
    </div>
  );




  return (
    <Dialog
      header={dialogHeader}
      visible={visible}
      style={{ width: "55vw", maxWidth: "800px" }}
      dismissableMask
      onHide={onHide}
      className="email-dialog"
      draggable={false}
      resizable={false}
    >
      <div className="email-dialog-container">
        {/* Show draft box if no previous emails or replying */}
        {(!emailThreads.length || replyingTo) && (
          <form
            onSubmit={handleSendEmail}
            className="flex flex-column gap-2 mb-3 p-3"
          >
            <h4 className="m-0 text-900 font-semibold" style={{ fontSize: '1rem' }}>Compose Email</h4>
            
            {/* To Field */}
            <div className="flex flex-column">
              <label className="text-sm font-medium text-700 mb-1">To</label>
              <InputText 
                  value={showName && showEmail ? `${showName} <${showEmail}>` : (showEmail || showName)} 
                  disabled 
                  className="w-full bg-gray-50 custom-input-height" 
              />
            </div>

            {/* CC Field */}
            <div className="flex flex-column">
              <label className="text-sm font-medium text-700 mb-1">CC</label>
              <MultiSelect
                value={selectedCCList}
                onChange={(e) => setSelectedCCList(e.value)}
                options={allSupervisors}
                optionLabel={(e) =>
                  `${e.first_name} ${e.last_name} - ${e.role_name}`
                }
                display="chip"
                filter
                placeholder="Select CC recipients"
                className="w-full custom-input-height"
                pt={{
                    root: { style: { minHeight: '38px' } }
                }}
              />
            </div>

            {/* Subject */}
            <div className="flex flex-column">
              <label htmlFor="customSubject" className="text-sm font-medium text-700 mb-1">Subject</label>
              <InputText
                id="customSubject"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Enter email subject"
                className="w-full custom-input-height"
              />
            </div>

            {/* Message Editor */}
            <div className="flex flex-column">
              <label htmlFor="customMessage" className="text-sm font-medium text-700 mb-1">Message</label>
              <Editor
                id="customMessage"
                value={customMessage}
                onTextChange={(e) => setCustomMessage(e.htmlValue)}
                style={{ height: "200px" }}
                pt={{
                    content: { style: { borderRadius: '0 0 6px 6px' } },
                    toolbar: { style: { borderRadius: '6px 6px 0 0' } }
                }}
              />
            </div>

            <div className="email-compose-signature-row mt-3">
              <div className="email-compose-signature-info">
                <div className="email-compose-signature-toggle">
                  <Checkbox
                    inputId="includeSignature"
                    checked={includeSignature}
                    onChange={(e) => setIncludeSignature(Boolean(e.checked))}
                  />
                  <label htmlFor="includeSignature" className="text-sm font-medium text-700">
                    Include signature
                  </label>
                </div>
                <p className="email-signature-note m-0 mt-1">
                  A saved signature will be appended to the email.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex justify-content-end gap-2 pt-2 mt-2">
              <Button
                label="Cancel"
                type="button"
                outlined
                severity="secondary"
                onClick={() => setReplyingTo(null)}
                className="px-3 py-2"
                style={{ fontSize: '0.9rem' }}
              />
              <Button
                label={accounts.length === 0 ? "Sign in to Send" : "Send Email"}
                icon={accounts.length === 0 ? "pi pi-sign-in" : "pi pi-send"}
                type="button"
                onClick={(e) => {
                    if (accounts.length === 0) {
                        getAccessToken();
                    } else {
                        handleSendEmail(e);
                    }
                }}
                className="customSave-button px-3 py-2"
                style={{ fontSize: '0.9rem' }}
                disabled={accounts.length > 0 && (!customSubject || !customMessage || !showEmail || loading)}
              />
            </div>
          </form>
        )}

        {/* Divider */}
        {emailThreads.length > 0 && <div className="flex align-items-center gap-2 my-3">
            <div className="flex-grow-1 border-top-1 surface-border"></div>
            <span className="text-500 font-medium" style={{ fontSize: '0.75rem' }}>HISTORY</span>
            <div className="flex-grow-1 border-top-1 surface-border"></div>
        </div>}

        {/* Email Threads */}
        <div className="email-history-container max-h-30rem overflow-auto">
            {accounts.length === 0 ? (
                <div className="text-center p-5 surface-50 border-round">
                    <i className="pi pi-microsoft text-3xl text-primary mb-3"></i>
                    <p className="text-700 m-0 mb-3 font-semibold">Sign in to view email history & send messages</p>
                    <Button 
                        label="Sign in with Microsoft" 
                        icon="pi pi-sign-in" 
                        className="p-button-outlined"
                        onClick={getAccessToken} 
                    />
                </div>
            ) : loading ? (
                 <div className="flex justify-content-center p-4">
                     <i className="pi pi-spin pi-spinner text-3xl"></i>
                 </div>
            ) : emailThreads.length === 0 ? (
            <div className="text-center p-4 surface-50 border-round">
                <i className="pi pi-inbox text-3xl text-400 mb-2"></i>
                <p className="text-500 m-0" style={{ fontSize: '0.9rem' }}>No previous email history found for {showEmail}.</p>
            </div>
            ) : (
                emailThreads.map((thread) => (
                    <div key={thread.thread_number} className="email-thread-card">
                        <div className="email-thread-header">
                          <h6 className="email-thread-title">{thread.subject}</h6>
                          <span className="email-thread-chip">Thread #{thread.thread_number}</span>
                        </div>

                        {thread.messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`email-message-card ${msg.direction === "incoming" ? "incoming" : "outgoing"}`}
                        >
                            <div className="email-message-header">
                            <span className="email-message-party">
                                {msg.direction === "outgoing" ? "You sent to " : "Received from "}
                              <span className="email-message-party-value">
                                    {msg.direction === "outgoing"
                                    ? (msg.to_email && msg.to_email.length > 0 ? msg.to_email.join(", ") : "Unknown")
                                    : (msg.from_email || "Unknown")}
                                </span>
                              <span className={`email-direction-badge ${msg.direction === "incoming" ? "received" : "sent"}`}>
                                {msg.direction === "incoming" ? "Received" : "Sent"}
                              </span>
                            </span>
                            <span className="email-message-date">{new Date(msg.send_at).toLocaleString()}</span>
                            </div>

                            <div className="email-body email-message-content">
                            {expandedMessages[msg._id] ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: msg.message || "No Content",
                                }}
                              />
                            ) : (
                              <div>
                                <div>{getMessagePreviewText(msg.message)}</div>
                                {getMessagePreviewImage(msg.message) && (
                                  <div className="email-inline-image-preview mt-3">
                                    <img
                                      src={getMessagePreviewImage(msg.message)}
                                      alt="Email preview"
                                      className="email-inline-image-preview-img"
                                      onError={(e) => {
                                        const fallbackSrc = getMessagePreviewImage(signaturePreviewHtml);
                                        if (fallbackSrc && e.currentTarget.src !== fallbackSrc) {
                                          e.currentTarget.src = fallbackSrc;
                                        } else {
                                          e.currentTarget.style.display = 'none';
                                        }
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            )}

                            {messageNeedsExpansion(msg.message) && (
                                <Button
                                label={
                                    expandedMessages[msg._id]
                                    ? "Show less"
                                    : "Show more"
                                }
                                link
                                className="p-0 mt-2"
                                style={{ fontSize: '0.85rem' }}
                                onClick={() => toggleExpand(msg._id)}
                                />
                            )}
                            </div>

                            {/* ✅ Reply button next to latest message only */}
                            {!replyingTo && i === 0 && (
                            <div className="mt-2 pt-2 border-top-1 surface-border text-right">
                                <Button
                                label="Reply to Thread"
                                icon="pi pi-reply"
                                size="small"
                                outlined
                                style={{ fontSize: '0.85rem' }}
                                onClick={() => {
                                    setReplyingTo(thread);
                                    setCustomSubject(`Re: ${thread.subject}`);
                                }}
                                />
                            </div>
                            )}
                        </div>
                        ))}
                    </div>
                ))
            )}
        </div>
      </div>
    </Dialog>
  );
};
export default EmailDialog;
